import { formValueSelector } from 'redux-form';
import { formName } from '../Constants/constants';
import { contactTypeOther, contactTypePerson } from '../Constants/contactTypes';
import { mapProvidersForSubmission } from './providerInputParser';
import { dateHelpers } from '@jauntin/utilities';
const { numberOfDays } = dateHelpers;

const formValues = formValueSelector(formName);

function getAdditionalCoverages(state) {
  const additionalCoverageTypes = [];
  const hostLiquor = formValues(state, 'ACHostLiquor');
  const personalAndAdvertisingLimit = formValues(
    state,
    'ACPersonalAndAdvertisingLimit',
  );
  const productsAndCompletedOperations = formValues(
    state,
    'ACProductsAndCompletedOperations',
  );
  const personalProperty = formValues(state, 'ACPersonalProperty');
  const sellingLiquor = formValues(state, 'ACSellingLiquor');
  const terrorism = formValues(state, 'ACTerrorism');
  const damageToRentedProperty = formValues(state, 'ACDamageToRentedProperty');

  if (hostLiquor)
    additionalCoverageTypes.push({ type: 'host-liquor', value: null });
  if (personalAndAdvertisingLimit)
    additionalCoverageTypes.push({
      type: 'personal-and-advertising-limit',
      value: null,
    });
  if (productsAndCompletedOperations)
    additionalCoverageTypes.push({
      type: 'products-and-completed-operations',
      value: null,
    });
  if (personalProperty)
    additionalCoverageTypes.push({ type: 'personal-property', value: null });
  if (sellingLiquor)
    additionalCoverageTypes.push({
      type: 'liquor-liability',
      value: sellingLiquor ? '1' : '0',
    });
  if (terrorism)
    additionalCoverageTypes.push({ type: 'terrorism', value: null });
  additionalCoverageTypes.push({
    type: 'damage-to-rented-property',
    value: damageToRentedProperty ? '1000000' : '250000',
  });

  return additionalCoverageTypes;
}

function getRenterContactType(state) {
  const indivOrCompany = formValues(state, 'insuredRenter');
  if (indivOrCompany === contactTypePerson) {
    return indivOrCompany;
  }
  const insuredCompanyType = formValues(state, 'insuredCompanyType');
  if (insuredCompanyType !== 'jointVenture') {
    return insuredCompanyType;
  }
  return formValues(state, 'jointVentureType');
}

function getPaymentBody(state) {
  const renterAddress = {
    address1: formValues(state, 'insuredAddress'),
    city: formValues(state, 'insuredCity'),
    state: formValues(state, 'insuredState'),
    country: formValues(state, 'insuredCountry'),
    zip: formValues(state, 'insuredZip'),
  };

  renterAddress.contactType = getRenterContactType(state);
  if (renterAddress.contactType === contactTypePerson) {
    renterAddress.contactFirstName = formValues(state, 'insuredFirstName');
    renterAddress.contactLastName = formValues(state, 'insuredLastName');
  } else {
    renterAddress.companyName = formValues(state, 'insuredCompany');
    renterAddress.companyType = formValues(state, 'insuredRenterType');
  }

  const insuranceContactAddress = {
    contactFirstName: formValues(state, 'contactFirstName'),
    contactLastName: formValues(state, 'contactLastName'),
    companyName: renterAddress.companyName,
    address1: renterAddress.address1,
    city: renterAddress.city,
    state: renterAddress.state,
    country: renterAddress.country,
    zip: renterAddress.zip,
    contactType: renterAddress.contactType,
  };

  if (!state.formPage.insuranceContact.contactSame) {
    insuranceContactAddress.address1 = formValues(state, 'contactAddress');
    insuranceContactAddress.city = formValues(state, 'contactCity');
    insuranceContactAddress.state = formValues(state, 'contactState');
    insuranceContactAddress.country = formValues(state, 'contactCountry');
    insuranceContactAddress.zip = formValues(state, 'contactZip');
    insuranceContactAddress.contactType = contactTypePerson;

    if (state.formPage.insuranceContact.contactRenter) {
      insuranceContactAddress.contactType = contactTypeOther;
      insuranceContactAddress.companyName = formValues(state, 'contactCompany');
    }
  }

  // Billing - send nothing if the address is the same, but otherwise,
  // get form values from 'payee' section
  let billingAddress = null;
  if (!state.formPage.paymentInfo.payeeSame) {
    billingAddress = {
      address1: formValues(state, 'payeeAddress'),
      city: formValues(state, 'payeeCity'),
      state: formValues(state, 'payeeState'),
      zip: formValues(state, 'payeeZip'),
      country: formValues(state, 'payeeCountry'),
    };
    if (state.formPage.paymentInfo.payeeRenter) {
      billingAddress.contactType = contactTypeOther;
      billingAddress.companyName = formValues(state, 'payeeCompany');
      billingAddress.contactFirstName = formValues(state, 'contactFirstName');
      billingAddress.contactLastName = formValues(state, 'contactLastName');
    } else {
      billingAddress.contactType = contactTypePerson;
      billingAddress.contactFirstName = formValues(state, 'payeeFirstName');
      billingAddress.contactLastName = formValues(state, 'payeeLastName');
    }
  }

  const cardInfo = {
    token: state.formPage.paymentInfo.token,
    cardType: state.formPage.paymentInfo.cardType,
    lastFour: state.formPage.paymentInfo.lastFour,
    nameOnCard: formValues(state, 'payeeCardName'),
    amountDetails: {
      totalAmount: state.formPage.priceValues.total,
      currency: 'USD',
    },
  };

  const days = numberOfDays(formValues(state, 'eventDates'));

  const values = {
    venueSearchType: formValues(state, 'venueSearchTypeRadio'),
    isKentuckyEntity: formValues(state, 'kentuckyStateEntity') === 'yes',
    isFederalEntity: formValues(state, 'federalEntity') === 'yes',
    isBlocked: state.formPage.venue.isBlocked === true,
    eventType: state.formPage.eventType?.selection?.identifier,
    lossClaims: state.formPage.eventSelectSecondary.lossClaims,
    security: state.formPage.eventSelectSecondary.security,
    promoter: state.formPage.eventSelectSecondary.promoter,
    gll: formValues(state, 'generalLiabilityCoverage'),
    name: formValues(state, 'eventName'),
    dates: formValues(state, 'eventDates').map((d) =>
      dateHelpers.dateOnlyStringFormat(d),
    ),
    eventFrequency: formValues(state, 'eventFrequencyField'),
    averageDailyAttendance: formValues(state, 'eventDailyGuests'),
    performers: mapProvidersForSubmission(
      formValues(state, 'eventPerformersCount'),
      formValues(state, 'performersFrequency'),
      formValues(state, 'performersList'),
      days,
    ),
    goods: mapProvidersForSubmission(
      formValues(state, 'eventGoods'),
      formValues(state, 'goodsVendorsFrequency'),
      formValues(state, 'goodsVendorsList'),
      days,
    ),
    food: mapProvidersForSubmission(
      formValues(state, 'eventFood'),
      formValues(state, 'foodVendorsFrequency'),
      formValues(state, 'foodVendorsList'),
      days,
    ),
    exhibitors: mapProvidersForSubmission(
      formValues(state, 'eventExhibition'),
      formValues(state, 'exhibitorsFrequency'),
      formValues(state, 'exhibitorsList'),
      days,
    ),
    additionalCoverage: getAdditionalCoverages(state),
    renterAddress: [renterAddress],
    cardInfo: [cardInfo],
    venueAddress: [
      {
        placeId: state.formPage.venue.selectedPlaceId || '',
        facilityCode: formValues(state, 'facilityCode') || '',
        venueCode: formValues(state, 'venueCode') || '',
        companyName:
          state.formPage.venue.selectedPlaceAddressComponents.companyName ||
          formValues(state, 'manualVenueName'),
        address1:
          state.formPage.venue.selectedPlaceAddressComponents.address1 ||
          formValues(state, 'manualVenueAddress1'),
        city:
          state.formPage.venue.selectedPlaceAddressComponents.city ||
          formValues(state, 'manualVenueCity'),
        state:
          state.formPage.venue.selectedPlaceAddressComponents.state ||
          formValues(state, 'manualVenueState'),
        country:
          state.formPage.venue.selectedPlaceAddressComponents.country ||
          formValues(state, 'manualVenueCountry'),
        zip:
          state.formPage.venue.selectedPlaceAddressComponents.zip ||
          formValues(state, 'manualVenueZip'),
        utcOffset: state.formPage.venue.selectedPlaceUtcOffset || 0,
        municipalTaxCode: formValues(state, 'venueMunicipalityCode'),
      },
    ],
    insuranceContactPhone: formValues(state, 'contactPhone'),
    insuranceContactEmail: formValues(state, 'contactEmail'),
    consentConfirmed: formValues(state, 'consentCheck'),
    excludedActivitiesConfirmed: formValues(state, 'excludedCheck'),
    sourceCode: state.formPage.referral.sourceCode,
    venueCode: state.formPage.referral.venueCode,
    facilityCode:
      state.formPage.referral.facilityCode || formValues(state, 'facilityCode'),
  };

  if (billingAddress) {
    values.billingAddress = [billingAddress];
  }

  values.insuranceContactAddress = [insuranceContactAddress];

  return values;
}

export default getPaymentBody;
